<template>
  <div class="cart-sidebar-overlay" @click.self="$emit('close')">
    <div class="cart-sidebar" :class="{ 'rtl': $i18n.locale === 'ar' }">
      <div class="cart-header">
        <h2>{{ $t('cartTitle') || 'My Cart' }}</h2>
        <button class="close-btn" @click="$emit('close')">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>

      <div v-if="store.cart.length > 0" class="cart-content">
        <div class="cart-items">
          <div v-for="(item, index) in store.cart" :key="index" class="cart-item">
            <img :src="item.image" alt="Product Image" class="item-img" />
            <div class="item-details">
              <div class="item-name">{{ item.name }}</div>
              <div class="item-specs">
                 <div v-for="sel in item.selected" :key="sel.id">
                    <span v-if="sel.colorName" class="spec-tag">
                      <span class="color-dot" :style="{ backgroundColor: sel.color }"></span>
                      {{ sel.colorName }}
                    </span>
                    <span v-if="sel.size" class="spec-tag">{{ sel.size }}</span>
                 </div>
              </div>
              <div class="item-price">{{ item.price }} DA</div>

              <div class="item-actions">
                <div class="qty-control">
                  <button @click="updateQty(index, -1)">-</button>
                  <span>{{ getCartItemQty(item) }}</span>
                  <button @click="updateQty(index, 1)">+</button>
                </div>
                <button class="remove-btn" @click="store.removeFromCart(index)">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="3 6 5 6 21 6"></polyline>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="cart-footer">
          <div class="subtotal">
            <span>{{ $t('subtotal') || 'Subtotal' }}:</span>
            <span>{{ cartSubtotal }} DA</span>
          </div>
          <button class="checkout-btn" @click="goToCheckout">
            {{ $t('checkout') || 'Checkout' }}
          </button>
        </div>
      </div>

      <div v-else class="empty-cart">
        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="9" cy="21" r="1"></circle>
          <circle cx="20" cy="21" r="1"></circle>
          <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
        </svg>
        <p>{{ $t('emptyCart') || 'Your cart is empty' }}</p>
        <button class="continue-btn" @click="$emit('close')">
          {{ $t('continueShopping') || 'Continue Shopping' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import store from '../store'

const router = useRouter()
const emit = defineEmits(['close'])

const getCartItemQty = (item) => {
   if (!item.selected || item.selected.length === 0) return 1
   return item.selected.reduce((acc, s) => acc + (s.qty || 1), 0)
}

const updateQty = (index, delta) => {
   const item = store.cart[index]
   if(item.selected && item.selected.length > 0) {
      if(item.selected[0].qty + delta > 0) {
         item.selected[0].qty += delta
         store.saveCart()
      }
   } else {
     // fallback if no selected property
     if(!item.qty) item.qty = 1
     if(item.qty + delta > 0) {
       item.qty += delta
       store.saveCart()
     }
   }
}

const cartSubtotal = computed(() => {
   return store.cart.reduce((acc, item) => acc + (item.price * getCartItemQty(item)), 0)
})

const goToCheckout = () => {
  emit('close')
  router.push('/cart')
}
</script>

<style scoped>
.cart-sidebar-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  z-index: 10000;
  display: flex;
  justify-content: flex-end;
}

.cart-sidebar {
  width: 100%;
  max-width: 400px;
  height: 100vh;
  background: white;
  display: flex;
  flex-direction: column;
  animation: slideInRight 0.3s ease-out forwards;
  box-shadow: -4px 0 15px rgba(0, 0, 0, 0.1);
}

.cart-sidebar.rtl {
  animation: slideInLeft 0.3s ease-out forwards;
}

@keyframes slideInRight {
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
}

@keyframes slideInLeft {
  from { transform: translateX(-100%); }
  to { transform: translateX(0); }
}

.cart-header {
  padding: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e2e8f0;
}

.cart-header h2 {
  font-size: 1.5rem;
  margin: 0;
  color: var(--primary);
}

.close-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #64748b;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background 0.2s;
}

.close-btn:hover {
  background: #f1f5f9;
  color: #ef4444;
}

.cart-content {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
}

.cart-items {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.cart-item {
  display: flex;
  gap: 15px;
  padding-bottom: 15px;
  border-bottom: 1px solid #f1f5f9;
}

.item-img {
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #f1f5f9;
}

.item-details {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.item-name {
  font-weight: 600;
  font-size: 1rem;
  margin-bottom: 5px;
  color: #1e293b;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.item-specs {
  display: flex;
  gap: 8px;
  font-size: 0.85rem;
  color: #64748b;
  margin-bottom: 5px;
  flex-wrap: wrap;
}

.spec-tag {
  display: flex;
  align-items: center;
  gap: 4px;
  background: #f8fafc;
  padding: 2px 6px;
  border-radius: 4px;
}

.color-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
  border: 1px solid #e2e8f0;
}

.item-price {
  font-weight: bold;
  color: var(--secondary);
  margin-bottom: 10px;
}

.item-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
}

.qty-control {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #f8fafc;
  border-radius: 6px;
  padding: 2px;
}

.qty-control button {
  width: 28px;
  height: 28px;
  border: none;
  background: white;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
}

.qty-control span {
  min-width: 20px;
  text-align: center;
  font-weight: 600;
}

.remove-btn {
  background: none;
  border: none;
  color: #ef4444;
  cursor: pointer;
  padding: 5px;
  border-radius: 4px;
  transition: background 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.remove-btn:hover {
  background: #fef2f2;
}

.cart-footer {
  padding: 20px;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
}

.subtotal {
  display: flex;
  justify-content: space-between;
  font-size: 1.2rem;
  font-weight: bold;
  color: #1e293b;
  margin-bottom: 15px;
}

.checkout-btn {
  width: 100%;
  padding: 15px;
  background: var(--secondary);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: bold;
  cursor: pointer;
  transition: background 0.3s;
}

.checkout-btn:hover {
  background: var(--accent);
}

.empty-cart {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #64748b;
  padding: 20px;
}

.empty-cart svg {
  margin-bottom: 15px;
  opacity: 0.5;
}

.empty-cart p {
  font-size: 1.1rem;
  margin-bottom: 20px;
}

.continue-btn {
  padding: 12px 24px;
  background: white;
  color: var(--secondary);
  border: 2px solid var(--secondary);
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
}

.continue-btn:hover {
  background: var(--secondary);
  color: white;
}
</style>
